import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { BulkActionGroup } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { useFilterQueryState } from 'src/components/MainFilterBar/useFilterQueryState';
import { useSalesBulkActionsState } from 'src/components/Sales/BulkActions/useSalesBulkActionsState';
import { useBulkDeallocateCancelSalesAction } from 'src/components/Sales/SalesActionDropdown/components/BulkDeallocateCancelSalesAction';
import { useBulkEditCommissionAction } from 'src/components/Sales/SalesActionDropdown/components/BulkEditCommissionAction';
import { useBulkEditTagsAction } from 'src/components/Sales/SalesActionDropdown/components/BulkEditTagsAction';
import { useBulkMarkAsFulfilledAction } from 'src/components/Sales/SalesActionDropdown/components/BulkMarkAsFulfilled';
import { useBulkMarkSaleAsPaid } from 'src/components/Sales/SalesActionDropdown/components/BulkMarkSaleAsPaid';
import { useBulkUpdateAutoFulfillAction } from 'src/components/Sales/SalesActionDropdown/components/BulkUpdateAutoFulfillAction';
import { useBulkUpdateBarcodes } from 'src/components/Sales/SalesActionDropdown/components/BulkUpdateBarcodesAction';
import { useBulkUpdateInternalNotesAction } from 'src/components/Sales/SalesActionDropdown/components/BulkUpdateInternalNotesAction';
import { useBulkUpdateSalesSettings } from 'src/components/Sales/SalesActionDropdown/components/BulkUpdateSalesGeneralSettings';
import { useExportSales } from 'src/components/Sales/SalesActionDropdown/components/LaunchExportSales/useExportSales';
import { Content } from 'src/contexts/ContentContext/Content';
import { vars } from 'src/core/themes';
import { ContentId } from 'src/utils/constants/contentId';
import { ActionOutboxEntityType, Event, SaleQuery } from 'src/WebApiController';

const useSaleActions = (
  isEventView: boolean,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
): BulkActionGroup | undefined => {
  const { filterQueryWithEventIds } = useFilterQueryState<SaleQuery>(
    ActionOutboxEntityType.Sale,
    undefined,
    isEventView
  );
  const bulkEditTags = useBulkEditTagsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined
  );
  const purchaseActions = [bulkEditTags.actionItems].flatMap((items) => items);
  if (isEventView || isEmpty(purchaseActions)) {
    return undefined;
  }
  return {
    id: ContentId.Sale,
    label: <Content id={ContentId.Sale} />,
    actions: purchaseActions,
  };
};

const useFulfillmentActions = (
  isLoading: boolean,
  setIsLoading: (_: boolean) => void,
  event?: Event
): BulkActionGroup | undefined => {
  const { filterQueryWithEventIds } = useSalesBulkActionsState();

  const bulkUpdateAutoFulfill = useBulkUpdateAutoFulfillAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkMarkAsFulfilled = useBulkMarkAsFulfilledAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const bulkDeallocateSales = useBulkDeallocateCancelSalesAction(
    filterQueryWithEventIds,
    true, // Deallocate only
    setIsLoading,
    isLoading,
    event
  );

  const bulkRejectSales = useBulkDeallocateCancelSalesAction(
    filterQueryWithEventIds,
    false, // Deallocate only
    setIsLoading,
    isLoading,
    event
  );

  const bulkUpdateBarcodesAction = useBulkUpdateBarcodes(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event
  );

  const settingActions = [
    bulkUpdateAutoFulfill.actionItems,
    bulkMarkAsFulfilled.actionItems,
    bulkDeallocateSales.actionItems,
    bulkRejectSales.actionItems,
    bulkUpdateBarcodesAction.actionItems,
  ].flatMap((items) => items);
  if (isEmpty(settingActions)) {
    return undefined;
  }
  return {
    id: ContentId.Fulfillment,
    label: <Content id={ContentId.Fulfillment} />,
    actions: settingActions,
  };
};

const useSettingsActions = (
  isEventView: boolean,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
): BulkActionGroup | undefined => {
  const { filterQueryWithEventIds } = useSalesBulkActionsState();

  const bulkUpdateGeneralSettings = useBulkUpdateSalesSettings(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined
  );

  const bulkUpdateInternalNotes = useBulkUpdateInternalNotesAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined
  );

  const bulkEditCommissionAction = useBulkEditCommissionAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined
  );

  const bulkMarkSaleAsPaid = useBulkMarkSaleAsPaid(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined
  );

  const exportSalesAction = useExportSales({
    style: { color: vars.color.textStrong },
    variant: 'text',
    shape: 'rect',
    textOnly: true,
  });

  const accountingActions = [
    bulkUpdateGeneralSettings.actionItems,
    bulkUpdateInternalNotes.actionItems,
    bulkEditCommissionAction.actionItems,
    bulkMarkSaleAsPaid.actionItems,
    exportSalesAction.actionItems,
  ].flatMap((items) => items);
  if (isEmpty(accountingActions)) {
    return undefined;
  }
  return {
    id: ContentId.Other,
    label: <Content id={ContentId.Other} />,
    actions: accountingActions,
  };
};

export const useSaleBulkActions = (isEventView: boolean): BulkActionGroup[] => {
  const [isLoading, setIsLoading] = useState(false);

  const actionGroups: BulkActionGroup[] = [];

  const purchaseActionGroup = useSaleActions(
    isEventView,
    isLoading,
    setIsLoading
  );

  if (purchaseActionGroup) {
    actionGroups.push(purchaseActionGroup);
  }

  const fulfillmentActionGroup = useFulfillmentActions(
    isLoading,
    setIsLoading,
    undefined
  );
  if (fulfillmentActionGroup) {
    actionGroups.push(fulfillmentActionGroup);
  }

  const settingsActionGroup = useSettingsActions(
    isEventView,
    isLoading,
    setIsLoading
  );
  if (settingsActionGroup) {
    actionGroups.push(settingsActionGroup);
  }

  return actionGroups;
};
